/* eslint-disable no-underscore-dangle */
import { ref, computed } from 'vue';

export const CALL_STATE = {
  IDLE: 'idle',
  RESOLVING: 'resolving',
  CALLING: 'calling',
  IN_CALL: 'in_call',
  INCOMING: 'incoming',
  ERROR: 'error',
};

const WACALLS_URL =
  window.chatwootConfig?.WACALLS_URL || 'http://localhost:8080';

const state = ref(CALL_STATE.IDLE);
const contactName = ref('');
const contactPhone = ref('');
const elapsed = ref(0);
const errorMsg = ref('');
const isMuted = ref(false);

let _pc = null;
let _mic = null;
let _callId = null;
let _sessionId = null;
let _t0 = null;
let _timer = null;
let _es = null;
let _incoming = null;
let _ringCtx = null;
let _ringTimer = null;

const isIdle = computed(() => state.value === CALL_STATE.IDLE);
const isResolving = computed(() => state.value === CALL_STATE.RESOLVING);
const isCalling = computed(() => state.value === CALL_STATE.CALLING);
const isInCall = computed(() => state.value === CALL_STATE.IN_CALL);
const isIncoming = computed(() => state.value === CALL_STATE.INCOMING);
const hasError = computed(() => state.value === CALL_STATE.ERROR);
const isActive = computed(
  () => state.value !== CALL_STATE.IDLE && state.value !== CALL_STATE.ERROR
);

function _waFetch(method, path, body) {
  const url = `${WACALLS_URL}${path}`;
  return fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => {
    if (!r.ok) throw new Error(`WaCalls ${method} ${path} → ${r.status}`);
    return r.text().then(t => (t ? JSON.parse(t) : {}));
  });
}

function _stopRing() {
  if (_ringTimer) {
    clearInterval(_ringTimer);
    _ringTimer = null;
  }
  if (_ringCtx) {
    try {
      _ringCtx.close();
    } catch {
      /* ignore */
    }
    _ringCtx = null;
  }
}

function _playRing() {
  _stopRing();
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    _ringCtx = new AC();
    const beep = () => {
      if (!_ringCtx) return;
      const o = _ringCtx.createOscillator();
      const g = _ringCtx.createGain();
      o.type = 'sine';
      o.frequency.value = 480;
      o.connect(g);
      g.connect(_ringCtx.destination);
      const t = _ringCtx.currentTime;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      o.start(t);
      o.stop(t + 0.95);
    };
    beep();
    _ringTimer = setInterval(beep, 2500);
  } catch {
    // ignore AudioContext errors
  }
}

function _startTimer() {
  _t0 = Date.now();
  state.value = CALL_STATE.IN_CALL;
  _timer = setInterval(() => {
    elapsed.value = Math.floor((Date.now() - _t0) / 1000);
  }, 1000);
}

function _stopTimer() {
  if (_timer) {
    clearInterval(_timer);
    _timer = null;
  }
  elapsed.value = 0;
  _t0 = null;
}

function _iceComplete(pc) {
  return new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') resolve();
    });
    setTimeout(resolve, 3000);
  });
}

function _connectAudio(ev) {
  let audio = document.getElementById('wacalls-audio');
  if (!audio) {
    audio = document.createElement('audio');
    audio.id = 'wacalls-audio';
    audio.autoplay = true;
    document.body.appendChild(audio);
  }
  audio.srcObject = ev.streams?.[0] || new MediaStream([ev.track]);
  audio.play().catch(() => {});
}

function _connectSSE() {
  if (_es) return;
  _es = new EventSource(`${WACALLS_URL}/api/events`);
  // eslint-disable-next-line no-use-before-define
  _es.onmessage = ev => {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    // eslint-disable-next-line no-use-before-define
    _handleEvent(msg);
  };
  _es.onerror = () => {
    /* ignore SSE errors */
  };
}

function _handleEvent(msg) {
  const type = msg.type || msg.event;

  if (_callId && (msg.id === _callId || msg.callId === _callId)) {
    if (type === 'call-ended' || type === 'ended' || msg.status === 'ended') {
      // eslint-disable-next-line no-use-before-define
      hangup();
    } else if (type === 'call-active' || msg.status === 'active') {
      if (state.value !== CALL_STATE.IN_CALL) _startTimer();
    }
    return;
  }

  if (type === 'incoming' || type === 'call-incoming') {
    if (_callId) return;
    _incoming = {
      sessionId: msg.sessionId || msg.session_id,
      callId: msg.id || msg.callId,
      name: msg.name || msg.peer || '',
      phone: msg.phone || msg.from || '',
    };
    contactName.value = _incoming.name || _incoming.phone;
    contactPhone.value = _incoming.phone;
    state.value = CALL_STATE.INCOMING;
    _playRing();
  }

  if (
    _incoming &&
    msg.id === _incoming.callId &&
    (type === 'call-ended' || type === 'ended')
  ) {
    _incoming = null;
    _stopRing();
    // eslint-disable-next-line no-use-before-define
    resetCall();
  }
}

async function initiateCall(sessionId, phone, name) {
  if (state.value !== CALL_STATE.IDLE) return;
  state.value = CALL_STATE.CALLING;
  errorMsg.value = '';
  contactName.value = name || phone;
  contactPhone.value = phone;
  _sessionId = sessionId;

  try {
    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    const pc = new RTCPeerConnection({ iceServers: [] });
    mic.getAudioTracks().forEach(t => pc.addTrack(t, mic));
    pc.ontrack = _connectAudio;

    const callResp = await _waFetch(
      'POST',
      `/api/sessions/${sessionId}/calls`,
      {
        phone,
        duration_ms: 300000,
      }
    );
    const callId = callResp.callId || callResp.id || callResp.call?.callId;

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await _iceComplete(pc);

    const ans = await _waFetch(
      'POST',
      `/api/sessions/${sessionId}/calls/${callId}/webrtc`,
      {
        sdp: pc.localDescription.sdp,
      }
    );
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: ans.sdp || ans.sdp_answer,
    });

    _pc = pc;
    _mic = mic;
    _callId = callId;

    _connectSSE();
  } catch (e) {
    state.value = CALL_STATE.ERROR;
    errorMsg.value = e.message;
  }
}

async function acceptIncoming() {
  const inc = _incoming;
  if (!inc) return;
  _incoming = null;
  _stopRing();
  state.value = CALL_STATE.CALLING;

  try {
    await _waFetch(
      'POST',
      `/api/sessions/${inc.sessionId}/calls/${inc.callId}/accept`,
      {}
    );
    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    const pc = new RTCPeerConnection({ iceServers: [] });
    mic.getAudioTracks().forEach(t => pc.addTrack(t, mic));
    pc.ontrack = _connectAudio;

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await _iceComplete(pc);

    const ans = await _waFetch(
      'POST',
      `/api/sessions/${inc.sessionId}/calls/${inc.callId}/webrtc`,
      {
        sdp: pc.localDescription.sdp,
      }
    );
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: ans.sdp || ans.sdp_answer,
    });

    _pc = pc;
    _mic = mic;
    _callId = inc.callId;
    _sessionId = inc.sessionId;

    _startTimer();
  } catch (e) {
    state.value = CALL_STATE.ERROR;
    errorMsg.value = e.message;
    _waFetch(
      'POST',
      `/api/sessions/${inc.sessionId}/calls/${inc.callId}/reject`,
      {}
    ).catch(() => {});
  }
}

function rejectIncoming() {
  const inc = _incoming;
  _incoming = null;
  _stopRing();
  if (inc) {
    _waFetch(
      'POST',
      `/api/sessions/${inc.sessionId}/calls/${inc.callId}/reject`,
      {}
    ).catch(() => {});
  }
  // eslint-disable-next-line no-use-before-define
  resetCall();
}

function toggleMute() {
  if (!_mic) return;
  const tracks = _mic.getAudioTracks();
  const enabled = tracks.length > 0 && tracks[0].enabled;
  tracks.forEach(t => {
    t.enabled = !enabled;
  });
  isMuted.value = enabled;
}

function hangup() {
  if (_callId && _sessionId) {
    _waFetch('DELETE', `/api/sessions/${_sessionId}/calls/${_callId}`).catch(
      () => {}
    );
  }
  // eslint-disable-next-line no-use-before-define
  _cleanup();
  state.value = CALL_STATE.IDLE;
}

function _cleanup() {
  _stopTimer();
  _stopRing();
  if (_mic) {
    _mic.getTracks().forEach(t => t.stop());
    _mic = null;
  }
  if (_pc) {
    try {
      _pc.close();
    } catch {
      /* ignore */
    }
    _pc = null;
  }
  _callId = null;
  _sessionId = null;
  _incoming = null;
  isMuted.value = false;
}

function resetCall() {
  _cleanup();
  state.value = CALL_STATE.IDLE;
  errorMsg.value = '';
  contactName.value = '';
  contactPhone.value = '';
}

function startSSE() {
  _connectSSE();
}

function stopSSE() {
  if (_es) {
    try {
      _es.close();
    } catch {
      /* ignore */
    }
    _es = null;
  }
}

export function useWahaCall() {
  return {
    state,
    contactName,
    contactPhone,
    elapsed,
    errorMsg,
    isMuted,
    isIdle,
    isResolving,
    isCalling,
    isInCall,
    isIncoming,
    hasError,
    isActive,
    initiateCall,
    acceptIncoming,
    rejectIncoming,
    toggleMute,
    hangup,
    resetCall,
    startSSE,
    stopSSE,
  };
}
